import type { JSX, ReactNode } from 'react'

import clsx from 'clsx'

import { Skeleton } from '@buffer-mono/popcorn'

import { BlankMetricsChartCard } from '../AllChannelsInsights/AllChannelsInsightsEmptyState/BlankMetricsChart'
import { BlankSummaryMetrics } from '../AllChannelsInsights/AllChannelsInsightsEmptyState/BlankSummaryMetrics'
import type { BlankSummaryMetric } from '../AllChannelsInsights/AllChannelsInsightsEmptyState/BlankSummaryMetrics'
import { BlankTopPostCards } from '../AllChannelsInsights/AllChannelsInsightsEmptyState/BlankTopPosts'
import { PerformanceTableSkeleton } from '../AllChannelsInsights/sections/AllChannelsPerformanceTable'
import { SectionCard } from '../components/SectionCard'

import postStyles from '../AllChannelsInsights/sections/AllChannelsTopPosts.module.css'
import chartStyles from '../AllChannelsInsights/sections/MetricsSection.module.css'
import styles from './InsightsSamplePreview.module.css'

function PlaceholderHeader({
  title,
  loading,
  action,
}: {
  title: string
  loading: boolean
  action?: ReactNode
}): JSX.Element {
  return (
    <SectionCard.Header>
      <SectionCard.Title>{title}</SectionCard.Title>
      {loading && action && <SectionCard.Actions>{action}</SectionCard.Actions>}
      <SectionCard.Subtitle className={getReservedRowClassName(loading)}>
        {loading && <Skeleton width={240} height={14} />}
      </SectionCard.Subtitle>
    </SectionCard.Header>
  )
}

function getReservedRowClassName(loading: boolean): string {
  return loading ? styles.growingRow : styles.collapsedRow
}

function PlaceholderAction(): JSX.Element {
  return <Skeleton width={144} height="var(--control-size-md)" />
}

function PlaceholderChartSection({
  title,
  loading,
  className,
}: {
  title: string
  loading: boolean
  className: string
}): JSX.Element {
  return (
    <SectionCard>
      <PlaceholderHeader
        title={title}
        loading={loading}
        action={<PlaceholderAction />}
      />
      <div
        className={clsx(
          chartStyles.chartWrapper,
          styles.placeholderChart,
          className,
        )}
      >
        <BlankMetricsChartCard />
      </div>
    </SectionCard>
  )
}

const PUBLIC_INSIGHTS_SUMMARY_METRICS: readonly BlankSummaryMetric[] = [
  { label: 'Posts', trend: 'up' },
  { label: 'Total Followers', trend: 'down' },
  { label: 'Reactions', trend: 'up' },
  { label: 'Comments', trend: 'down' },
]

/**
 * The five section boxes of the public sample, without data: same titles, same
 * order and the same header rows as the loaded sections, so every section lands
 * where its placeholder stood when Buffer's data arrives. Used both before
 * preview mode is turned on and while the sample loads; `loading` only swaps the
 * static placeholders for skeletons.
 */
export function PublicInsightsPlaceholderSections({
  loading = false,
}: {
  loading?: boolean
}): JSX.Element {
  return (
    <>
      <BlankSummaryMetrics
        loading={loading}
        metrics={PUBLIC_INSIGHTS_SUMMARY_METRICS}
        subtitleClassName={getReservedRowClassName(loading)}
      />
      <SectionCard>
        <PlaceholderHeader
          title="Top Posts"
          loading={loading}
          action={<PlaceholderAction />}
        />
        <div className={postStyles.cardsGridContainer}>
          <div className={clsx(postStyles.cardsGrid, styles.placeholderPosts)}>
            <BlankTopPostCards />
          </div>
        </div>
        <div
          aria-hidden
          className={clsx(
            styles.reservedPagination,
            getReservedRowClassName(loading),
          )}
        />
      </SectionCard>
      <SectionCard>
        <PlaceholderHeader title="Performance" loading={loading} />
        <div
          className={clsx(
            styles.placeholderTable,
            !loading && styles.staticPlaceholder,
          )}
        >
          <PerformanceTableSkeleton />
        </div>
        <div
          aria-hidden
          className={clsx(
            styles.reservedPagination,
            getReservedRowClassName(loading),
          )}
        />
      </SectionCard>
      <PlaceholderChartSection
        title="Followers"
        loading={loading}
        className={styles.placeholderFollowersChart}
      />
      <PlaceholderChartSection
        title="Posts"
        loading={loading}
        className={styles.placeholderPostsChart}
      />
    </>
  )
}
